import { Component, type ReactNode } from 'react';

/**
 * Catches render-time errors in its subtree so one bad view can't blank the
 * whole app. Shows a readable message (and the error text) instead of a white
 * screen, and keeps the rest of the page usable.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; label?: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error('ErrorBoundary caught an error', this.props.label ?? '', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="max-w-3xl mx-auto my-8 p-5 rounded-lg border"
          style={{ borderColor: 'var(--status-err)', background: 'color-mix(in srgb, var(--status-err) 8%, transparent)' }}>
          <div className="font-semibold mb-1" style={{ color: 'var(--status-err)' }}>
            Something went wrong displaying this {this.props.label ?? 'view'}.
          </div>
          <div className="text-sm text-axis-ash mb-2">
            The rest of the app is still usable. Try refreshing; if it keeps happening, share this error:
          </div>
          <pre className="text-xs whitespace-pre-wrap break-words p-2 rounded"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-strong)' }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
